import { Fragment, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { Accordion, Button, Icon, Input, Message } from 'semantic-ui-react';
import { QRCodeSVG } from 'qrcode.react';

import banner from '../../images/login-banner.png';
import { PATHS } from '../../paths';
import { LOG_APPLICATION_EVENT } from '../../mutations/LOG_APPLICATION_EVENT';
import styles from './index.module.css';

/**
 * Public install guide for the Joyya Android/iOS apps (`/app`, aliased from `/android` and
 * `/ios`). Reachable without being signed in — it exists specifically for people who don't have
 * the app yet — so like Support/PrivacyPolicy it renders its own navy/white document shell
 * rather than sitting inside the authenticated app's themed layout.
 *
 * Android goes out via Google Play **closed testing**: a visitor must join the
 * swbcandjoyya Google Group before Play's opt-in link will resolve for them, and group
 * membership can take up to an hour to reach Play. iOS has no such gate — the App Store build
 * is already public — so iOS visitors skip straight to install.
 */

const GROUP_URL = 'https://groups.google.com/g/swbcandjoyya/about';
// Confirmed against Play Console (Testing → Closed testing → Testers → "How testers join your test").
const OPT_IN_URL = 'https://play.google.com/apps/testing/com.joyya.android';
const PLAY_URL = 'https://play.google.com/store/apps/details?id=com.joyya.android';
const IOS_URL = 'https://apps.apple.com/us/app/joyya/id6761905788';
const STORE_KEY = 'joyya.getTheApp';

type Platform = 'android' | 'ios' | 'desktop';

interface StoredState {
  step?: number;
  code?: string;
  email?: string;
  sessionId?: string;
}

function readStore(): StoredState {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeStore(patch: Partial<StoredState>) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify({ ...readStore(), ...patch }));
  } catch {
    // localStorage can be unavailable (private browsing) — the page still works, it just
    // won't remember progress across visits.
  }
}

function makeSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * The User-Agent always wins over `?p=`. Someone on an iPhone who scans a printed `/android`
 * poster (a hint carried through the `/android` → `/app?p=android` redirect) must still land on
 * iOS instructions — `?p=` only decides what a *desktop* visitor sees, and doubles as a QA
 * override (`?p=android` on a laptop, for example).
 */
export function resolvePlatform(userAgent: string, pHint: string | null): Platform {
  const ua = (userAgent || '').toLowerCase();
  if (ua.includes('android')) return 'android';
  if (/iphone|ipad|ipod/.test(ua)) return 'ios';
  if (pHint === 'android' || pHint === 'ios') return pHint;
  return 'desktop';
}

function Step({ number, title, children }: { number: number; title: string; children: React.ReactNode }) {
  return (
    <div className={styles.step}>
      <div className={styles.stepHeader}>
        <span className={styles.stepNumber}>{number}</span>
        <h2 className={styles.stepTitle}>{title}</h2>
      </div>
      <div className={styles.stepBody}>{children}</div>
    </div>
  );
}

const TROUBLESHOOTING_ENTRIES = [
  {
    title: "It says the app doesn't exist",
    body: "That's normal at first. Google can take up to an hour to notice you've joined the group. "
      + "Come back to this page later and try the same button again — you don't need to start over.",
  },
  {
    title: 'Which Google account am I using?',
    body: 'On your phone, open the Play Store app, tap your profile picture in the top right corner, '
      + 'and check the email address shown there. That has to be the same Gmail address you used to '
      + 'join the group.',
  },
  {
    title: 'I joined but nothing happened',
    body: "That's expected — joining the group is step 1, and Play only lets you in after step 2. "
      + 'Go back to step 2 above and tap "Become a tester".',
  },
  {
    title: "I don't have a Google account",
    body: "You'll need one to install an Android app from the Play Store. You can create a free "
      + "one at accounts.google.com, then come back and start from step 1.",
  },
];

function Troubleshooting() {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  return (
    <>
      <h3 className={styles.sectionTitle}>Troubleshooting</h3>
      <Accordion className={styles.troubleshooting}>
        {TROUBLESHOOTING_ENTRIES.map((entry, index) => (
          <Fragment key={entry.title}>
            <Accordion.Title
              active={activeIndex === index}
              index={index}
              onClick={() => setActiveIndex(activeIndex === index ? null : index)}
            >
              <Icon name="dropdown" />
              {entry.title}
            </Accordion.Title>
            <Accordion.Content active={activeIndex === index}>
              <p className={styles.stepBody}>{entry.body}</p>
            </Accordion.Content>
          </Fragment>
        ))}
      </Accordion>
    </>
  );
}

function HelpFallback({
  defaultEmail,
  onSubmit,
}: {
  defaultEmail: string;
  onSubmit: (email: string) => void;
}) {
  const [email, setEmail] = useState(defaultEmail);
  const [sent, setSent] = useState(false);

  if (sent) {
    return (
      <div className={styles.fallback}>
        <Message positive>Thanks — we'll be in touch to help you get set up.</Message>
      </div>
    );
  }

  return (
    <div className={styles.fallback}>
      <p className={styles.fallbackTitle}>Still stuck?</p>
      <p className={styles.stepBody}>Leave your email and we'll add you manually.</p>
      <Input
        fluid
        className={styles.emailField}
        placeholder="you@example.com"
        value={email}
        onChange={(_, { value }) => setEmail(value)}
      />
      <Button
        className={styles.primaryBtn}
        fluid
        disabled={!email.includes('@')}
        onClick={() => {
          onSubmit(email);
          setSent(true);
        }}
      >
        Ask for help
      </Button>
    </div>
  );
}

function DesktopPanel({ url, onContinueAnyway }: { url: string; onContinueAnyway: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className={styles.desktopPanel}>
      <p className={styles.stepBody}>
        Do this on your phone — it won't work from a computer. Scan this with your phone's camera,
        or copy the link and send it to yourself.
      </p>
      <div className={styles.qr}>
        <QRCodeSVG value={url} size={200} level="M" />
      </div>
      <Button
        className={styles.primaryBtn}
        fluid
        size="large"
        onClick={() => {
          navigator.clipboard?.writeText(url).catch(() => {});
          setCopied(true);
        }}
      >
        {copied ? 'Copied!' : 'Copy link'}
      </Button>
      <button type="button" className={styles.secondaryBtn} onClick={onContinueAnyway}>
        Continue here anyway →
      </button>
    </div>
  );
}

function CodeSection({ code, onCopy }: { code: string; onCopy: () => void }) {
  if (!code) {
    return (
      <p className={styles.stepBody}>
        Ask your community leader for your code, or visit <Link to={PATHS.SUPPORT}>Support</Link>.
      </p>
    );
  }
  return (
    <div className={styles.codeChip}>
      <span className={styles.codeChipValue}>{code}</span>
      <button
        type="button"
        className={styles.codeChipCopy}
        onClick={() => {
          navigator.clipboard?.writeText(code).catch(() => {});
          onCopy();
        }}
        aria-label="Copy code"
      >
        <Icon name="copy" size="large" />
      </button>
    </div>
  );
}

function GetTheApp() {
  const { code: codeParam } = useParams<{ code?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const pHint = searchParams.get('p');
  const src = searchParams.get('src');

  const stored = useRef(readStore()).current;
  const [sessionId] = useState(() => stored.sessionId ?? makeSessionId());
  const [code, setCode] = useState(() => codeParam || stored.code || '');
  const [email, setEmail] = useState(() => stored.email || '');
  const [continuedAnyway, setContinuedAnyway] = useState(false);
  const [logEvent] = useMutation(LOG_APPLICATION_EVENT);

  const platform: Platform = resolvePlatform(navigator.userAgent, pHint);

  const urlStepRaw = Number(searchParams.get('step'));
  const urlStep = urlStepRaw >= 1 && urlStepRaw <= 3 ? urlStepRaw : null;
  const step = platform === 'ios' ? 3 : (urlStep ?? (stored.step && stored.step >= 1 && stored.step <= 3 ? stored.step : 1));

  const fireLog = (eventType: string, extra: Record<string, string | null | undefined> = {}) => {
    logEvent({
      variables: {
        eventType,
        sessionId,
        platform,
        code: code || null,
        userAgent: (navigator.userAgent || '').slice(0, 256),
        ...extra,
      },
    }).catch(() => {
      // Analytics must never block or break the page.
    });
  };

  useEffect(() => {
    writeStore({ sessionId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (codeParam) {
      setCode(codeParam);
      writeStore({ code: codeParam });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeParam]);

  const hasLoggedPageView = useRef(false);
  useEffect(() => {
    if (hasLoggedPageView.current) return;
    hasLoggedPageView.current = true;
    fireLog('PAGE_VIEWED', { detail: src ?? null });
    // Seed the URL's ?step= from stored/derived progress so a hard refresh or a shared link
    // has something to resume from, without polluting browser history with an extra entry.
    if (urlStep == null) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('step', String(step));
        return next;
      }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToStep = (next: number) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('step', String(next));
      return params;
    });
    writeStore({ step: next });
    fireLog('STEP_VIEWED', { detail: String(next) });
  };

  const currentUrl = `${window.location.origin}${PATHS.GET_THE_APP}${code ? `/${code}` : ''}`;

  if (platform === 'desktop' && !continuedAnyway) {
    return (
      <div className={styles.page}>
        <div className={styles.brandBar}>
          <img src={banner} alt="Joyya" className={styles.brandLogo} />
        </div>
        <div className={styles.content}>
          <div className={styles.doc}>
            <h1 className={styles.docTitle}>Get the Joyya app</h1>
            <hr className={styles.divider} />
            <DesktopPanel url={currentUrl} onContinueAnyway={() => setContinuedAnyway(true)} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.brandBar}>
        <img src={banner} alt="Joyya" className={styles.brandLogo} />
      </div>
      <div className={styles.content}>
        <div className={styles.doc}>
          <h1 className={styles.docTitle}>Get the Joyya app</h1>
          <p className={styles.docSubtitle}>
            {platform === 'ios'
              ? 'A few taps and you\'ll be signed in.'
              : "Three steps — it's easier than it looks."}
          </p>
          <hr className={styles.divider} />

          {platform === 'android' && step === 1 && (
            <Step number={1} title="Join the group">
              <p>
                Joyya's Android app is early access, so first you need to join our Google Group.
              </p>
              <Button
                className={styles.primaryBtn}
                fluid
                size="huge"
                as="a"
                href={GROUP_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => fireLog('GROUP_OPENED')}
              >
                Join the group
              </Button>
              <p className={styles.stepBody} style={{ marginTop: '1.25em' }}>
                Which Gmail address did you use to join?
              </p>
              <Input
                fluid
                className={styles.emailField}
                placeholder="you@gmail.com"
                value={email}
                onChange={(_, { value }) => setEmail(value)}
              />
              <Button
                className={styles.primaryBtn}
                fluid
                size="huge"
                disabled={!email.includes('@')}
                onClick={() => {
                  writeStore({ email });
                  fireLog('EMAIL_SUBMITTED', { email });
                  goToStep(2);
                }}
              >
                Next
              </Button>
            </Step>
          )}

          {platform === 'android' && step === 2 && (
            <Step number={2} title="Become a tester">
              <p>
                On your phone, open the Play Store, tap your profile picture in the top right, and
                check it says <strong>{email || 'the Gmail address you just joined with'}</strong>.
              </p>
              <Message info className={styles.waitNote}>
                Google can take up to an hour to notice you joined the group. If the next button
                says the app doesn't exist, that's normal — come back to this page later and try
                again.
              </Message>
              <Button
                className={styles.primaryBtn}
                fluid
                size="huge"
                as="a"
                href={OPT_IN_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => fireLog('OPT_IN_OPENED')}
              >
                Become a tester
              </Button>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={() => {
                  fireLog('STORE_OPENED', { detail: 'already-opted-in' });
                  goToStep(3);
                }}
              >
                I've already done this
              </button>
            </Step>
          )}

          {step === 3 && (
            <Step number={3} title={platform === 'ios' ? 'Install and sign in' : 'Install and sign in'}>
              <p>
                {platform === 'ios'
                  ? 'Install Joyya from the App Store.'
                  : 'Now install Joyya from the Play Store.'}
              </p>
              <Button
                className={styles.primaryBtn}
                fluid
                size="huge"
                as="a"
                href={platform === 'ios' ? IOS_URL : PLAY_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => fireLog('STORE_OPENED')}
              >
                {platform === 'ios' ? 'Open the App Store' : 'Open the Play Store'}
              </Button>
              <p className={styles.stepBody} style={{ marginTop: '1.5em' }}>
                When you sign up, enter this community code so you land in the right place:
              </p>
              <CodeSection code={code} onCopy={() => fireLog('CODE_COPIED')} />
              {platform === 'android' && (
                <button type="button" className={styles.secondaryBtn} onClick={() => goToStep(1)}>
                  ← Back to step 1
                </button>
              )}
            </Step>
          )}

          <Troubleshooting />
          <HelpFallback
            defaultEmail={email}
            onSubmit={(submittedEmail) => fireLog('HELP_REQUESTED', { email: submittedEmail })}
          />
        </div>
      </div>
    </div>
  );
}

export { GetTheApp };
